import { canUseDirect, assertSourceUrl, getSourcePolicy, isDynamicSource } from "./SourceRegistry.ts";
import {
  NetworkError,
  type HttpRequest,
  type ImageRequest,
  type YomiriHttpClient as TransportContract,
} from "./types.ts";

export interface RouterSession {
  authenticated: boolean;
  workerEnabled: boolean;
  preferWorker?: boolean;
}
export interface RouterOptions {
  direct: TransportContract;
  extension: TransportContract & { isAvailable(): Promise<boolean>; markUnavailable?(): void };
  worker?: TransportContract & {
    isAvailable(): boolean;
    cooldownError(): NetworkError | undefined;
    canDeliverImagesDirect?(): boolean;
  };
  personalProxy: TransportContract & { isAvailable(): boolean };
  development?: TransportContract & { supports(sourceId: string): boolean };
  session?: () => RouterSession;
}

/** Called only on cache misses. Source errors never trigger a second request through another transport. */
export class TransportRouter {
  private readonly directCooldown = new Map<string, number>();
  constructor(private readonly options: RouterOptions) {}

  text(request: HttpRequest & { responseType: "text" }) {
    return this.route(request, (transport) => transport.text(request));
  }

  bytes(request: HttpRequest & { responseType: "bytes" }) {
    return this.route(request, (transport) => transport.bytes(request));
  }

  prepareImage(request: ImageRequest) {
    // <img> can display cross-origin resources without reading their bytes through fetch/CORS.
    if (
      !request.referer &&
      !request.requiresSession &&
      !getSourcePolicy(request.sourceId).requiresSession &&
      Object.keys(request.headers ?? {}).every((name) => name.toLowerCase() === "accept")
    )
      return this.options.direct.prepareImage(request);
    return this.route(request, (transport) => transport.prepareImage(request), true);
  }

  private async route<T>(
    request: HttpRequest | ImageRequest,
    execute: (transport: TransportContract) => Promise<T>,
    image = false,
  ): Promise<T> {
    assertSourceUrl(request.sourceId, request.url);
    if (request.signal?.aborted) throw new NetworkError("ABORTED", "Solicitud cancelada.");
    if (this.options.development?.supports(request.sourceId)) {
      return await execute(this.options.development);
    }
    if (canUseDirect(request)) {
      const origin = new URL(request.url).origin;
      if ((this.directCooldown.get(origin) ?? 0) <= Date.now()) {
        try {
          return await execute(this.options.direct);
        } catch (error) {
          // A browser CORS/network rejection has no HTTP status. Never retry bans,
          // challenges, quota errors or cancellations through a different connection.
          if (!(error instanceof NetworkError) || error.code !== 'HTTP_ERROR' || error.status !== undefined)
            throw error;
          this.directCooldown.set(origin, Date.now() + 300000);
        }
      }
    }
    if (await this.options.extension.isAvailable()) {
      if (request.signal?.aborted) throw new NetworkError("ABORTED", "Solicitud cancelada.");
      try {
        return await execute(this.options.extension);
      } catch (error) {
        if (error instanceof Error && /(?:fuente|source).*(?:no (?:está )?autorizad|not authoriz)/i.test(error.message))
          throw new NetworkError('PERMISSION_REQUIRED', 'La versión de Yomiri Bridge instalada no reconoce esta fuente. Actualizá Bridge y recargá Yomiri.', undefined, 'extension');
        if (!(error instanceof NetworkError)) throw error;
        if (error.code !== "BRIDGE_UNAVAILABLE")
          throw new NetworkError(error.code, error.message, error.status, "extension");
        this.options.extension.markUnavailable?.();
      }
    }
    const isDynamic = isDynamicSource(request.sourceId);
    throw new NetworkError(
      "BRIDGE_UNAVAILABLE",
      "Esta fuente necesita Yomiri Bridge 2.3 (o superior) conectado en este navegador. Instalá o habilitá la extensión y tocá Reintentar.",
    );
  }
}
